/**
 * The museum's open/closed state, in Europe/Zurich time, from the schedule payload that
 * snippets/visit-hours-payload-mab.liquid renders.
 *
 * describeVisitHours() is pure and exported for scripts/test-visit-hours-state.mjs. The
 * visit-hours-state element writes its line into the element named by data-target and marks it
 * data-state="open|closed". The server leaves the target's own content in place, so a page whose
 * script does not run stays complete and correct.
 */

function parse(text) {
try {
  return JSON.parse(text);
} catch {
  return null;
}
}

/**
 * Hides list items whose end date has already passed in Zurich time.
 * Dates are formatted server-side in Liquid; this is the cache guard only.
 */
function formatUpcomingDates() {
const todayZurich = zurichIso(new Date());

for (const el of document.querySelectorAll('.visit-special-day__date')) {
  const date = el.getAttribute('datetime');
  const dateEnd = el.dataset.dateEnd;
  if (!date) continue;

  const endDate = dateEnd || date;
  const row = el.closest('.product-spec-row');
  if (row && endDate < todayZurich) {
    row.hidden = true;
  }
}
}

/**
 * The schedule rows must carry complete data for every open row. Special days are
 * optional and never block the status line.
 * @param {object} payload
 * @returns {boolean}
 */
export function isComplete(payload) {
if (!Array.isArray(payload.rows) || payload.rows.length === 0) return false;

for (const row of payload.rows) {
  if (!row.validFrom || !row.validThrough) return false;
  if (row.closed) continue;
  if (!Array.isArray(row.days) || row.days.length === 0) return false;
  if (!row.opens || !row.closes) return false;
}

return true;
}

/**
 * Returns the calendar date in Europe/Zurich as YYYY-MM-DD.
 * en-CA uses ISO date format (YYYY-MM-DD).
 * @param {Date} date
 * @returns {string}
 */
function zurichIso(date) {
return new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Zurich',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(date);
}

/**
 * Returns current time in Europe/Zurich as minutes since midnight.
 * @param {Date} date
 * @returns {number}
 */
function zurichMinutes(date) {
const parts = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Zurich',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}).formatToParts(date);
const hour = Number(parts.find((p) => p.type === 'hour').value);
const minute = Number(parts.find((p) => p.type === 'minute').value);
return hour * 60 + minute;
}

/**
 * Returns the English weekday name for Europe/Zurich — matches the day names
 * stored in payload.rows[].days.
 * @param {Date} date
 * @returns {string} e.g. "Tuesday"
 */
function zurichWeekday(date) {
return new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Zurich',
  weekday: 'long',
}).format(date);
}

/**
 * A season's bounds carry a year, and the museum's seasons repeat annually, so only
 * the month and day decide which season a date falls in.
 * @param {string} iso
 * @returns {number} MMDD as a comparable integer
 */
function monthDay(iso) {
return Number(iso.slice(5, 7) + iso.slice(8, 10));
}

/**
 * @param {object} row
 * @param {string} isoDate - YYYY-MM-DD in Zurich
 * @returns {boolean}
 */
function inSeason(row, isoDate) {
const from = monthDay(row.validFrom);
const through = monthDay(row.validThrough);
const now = monthDay(isoDate);
if (from <= through) return now >= from && now <= through;
return now >= from || now <= through;
}

/**
 * @param {string} time - HH:MM
 * @returns {number} minutes since midnight
 */
function minutes(time) {
const [h, m] = time.split(':');
return Number(h) * 60 + Number(m);
}

/**
 * Finds the special day entry that covers isoDate (a date may fall in a range).
 * @param {Array} specialDays
 * @param {string} isoDate
 * @returns {object|null}
 */
function findSpecialDay(specialDays, isoDate) {
for (const day of specialDays) {
  const end = day.dateEnd || day.date;
  if (day.date <= isoDate && isoDate <= end) return day;
}
return null;
}

/**
 * Finds the regular hours row active for the given date and weekday.
 * @param {Array} rows
 * @param {string} isoDate
 * @param {string} weekday - English day name
 * @returns {object|null}
 */
function findHoursRow(rows, isoDate, weekday) {
for (const row of rows) {
  if (row.closed) continue;
  if (!inSeason(row, isoDate)) continue;
  if (row.days.includes(weekday)) return row;
}
return null;
}

/**
 * Searches forward from startOffset days to find the next opening, checking
 * special days first, then regular hours.
 * Returns { date: Date, opens: string, isTomorrow: boolean } or null.
 * @param {object} payload
 * @param {string} todayIso
 * @param {number} startOffset - days ahead to begin searching (1 = tomorrow)
 * @returns {{date: Date, opens: string, isTomorrow: boolean}|null}
 */
function nextOpening(payload, todayIso, startOffset) {
const todayMs = new Date(`${todayIso}T12:00:00Z`).getTime();

for (let d = startOffset; d <= 14; d++) {
  const futureDate = new Date(todayMs + d * 86_400_000);
  const futureIso = zurichIso(futureDate);

  const special = findSpecialDay(payload.specialDays || [], futureIso);
  if (special) {
    if (special.closed) continue;
    if (special.opens && special.closes) {
      return { date: futureDate, opens: special.opens, isTomorrow: d === 1 };
    }
    continue;
  }

  const weekday = zurichWeekday(futureDate);
  const row = findHoursRow(payload.rows, futureIso, weekday);
  if (row) return { date: futureDate, opens: row.opens, isTomorrow: d === 1 };
}

return null;
}

/**
 * Formats the "closed, next opening is X" line (for a regular closed day or after hours).
 * @param {{date: Date, opens: string, isTomorrow: boolean}|null} next
 * @param {object} strings
 * @param {string} locale
 * @returns {string}
 */
function formatClosed(next, strings, locale) {
if (!next) return strings.closedNow;
if (next.isTomorrow) {
  return strings.closedOpensTomorrow.replace('__TIME__', next.opens);
}
const dayName = new Intl.DateTimeFormat(locale, {
  timeZone: 'Europe/Zurich',
  weekday: 'long',
}).format(next.date);
return strings.closedOpensDay.replace('__DAY__', dayName).replace('__TIME__', next.opens);
}

/**
 * Formats the "closed today, next opening is X" line (for a special closed day).
 * @param {{date: Date, opens: string, isTomorrow: boolean}|null} next
 * @param {object} strings
 * @param {string} locale
 * @returns {string}
 */
function formatClosedToday(next, strings, locale) {
if (!next) return strings.closedNow;
if (next.isTomorrow) {
  return strings.closedTodayOpensTomorrow.replace('__TIME__', next.opens);
}
const dayName = new Intl.DateTimeFormat(locale, {
  timeZone: 'Europe/Zurich',
  weekday: 'long',
}).format(next.date);
return strings.closedTodayOpensDay
  .replace('__DAY__', dayName)
  .replace('__TIME__', next.opens);
}

/**
 * Whether the museum is open at `now`, and the line saying so.
 * Returns null when the regular schedule is incomplete, so callers render nothing.
 * @param {object} payload - rows, specialDays, locale, strings
 * @param {Date} now
 * @returns {{ line: string, open: boolean } | null}
 */
export function describeVisitHours(payload, now) {
  if (!isComplete(payload)) return null;

  const todayIso = zurichIso(now);
  const minutesNow = zurichMinutes(now);
  const weekday = zurichWeekday(now);
  const { strings, locale } = payload;
  const specialDays = payload.specialDays || [];
  const closed = (line) => ({ line, open: false });
  const specialToday = findSpecialDay(specialDays, todayIso);

  if (specialToday) {
    const prefix = specialToday.label ? `${specialToday.label} · ` : '';
    if (specialToday.closed) {
      return closed(prefix + formatClosedToday(nextOpening(payload, todayIso, 1), strings, locale));
    }
    if (specialToday.opens && specialToday.closes) {
      if (minutesNow < minutes(specialToday.opens)) {
        return closed(strings.opensTodayAt.replace('__TIME__', specialToday.opens));
      }
      if (minutesNow < minutes(specialToday.closes)) {
        return { line: prefix + strings.openUntil.replace('__TIME__', specialToday.closes), open: true };
      }
      return closed(formatClosed(nextOpening(payload, todayIso, 1), strings, locale));
    }
    return closed(prefix + formatClosedToday(nextOpening(payload, todayIso, 1), strings, locale));
  }

  const row = findHoursRow(payload.rows, todayIso, weekday);
  if (row) {
    if (minutesNow < minutes(row.opens)) return closed(strings.opensTodayAt.replace('__TIME__', row.opens));
    if (minutesNow < minutes(row.closes)) {
      return { line: strings.openUntil.replace('__TIME__', row.closes), open: true };
    }
  }
  return closed(formatClosed(nextOpening(payload, todayIso, 1), strings, locale));
}

if (typeof HTMLElement !== 'undefined') {
  class VisitHoursState extends HTMLElement {
    connectedCallback() {
      const targetId = this.dataset.target;
      if (!targetId) return;

      const target = document.getElementById(targetId);
      const payloadNode = document.querySelector(`[data-visit-hours-payload="${targetId}"]`);
      if (!target || !payloadNode) return;

      const payload = parse(payloadNode.textContent);
      if (!payload) return;

      formatUpcomingDates();

      const result = describeVisitHours(payload, new Date());
      if (!result) return;

      target.textContent = result.line;
      target.dataset.state = result.open ? 'open' : 'closed';
    }
  }

  if (!customElements.get('visit-hours-state')) {
    customElements.define('visit-hours-state', VisitHoursState);
  }
}
